import React, { useContext, useEffect, useState } from "react"
import { Div, Text, Row, Col } from "atomize"
import { motion } from "framer-motion"
import { toast } from "react-toastify"
import Container from "../atoms/Container"
import Paragraph from "../atoms/Paragraph"
import ToggleChip from "../atoms/ToggleChip"
import TextInput from "../inputs/TextInput"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import { theme } from "../settings/config"
import { profileContext } from "../providers/profile.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import SchoolCourtsEditor from "../school/SchoolCourtsEditor"
import { DEFAULT_SPORT } from "../utils/func.utils"
import { inviteEntryText } from "../utils/school.utils"
import { RadioBlock } from "../school/SheetBlocks"
import { useBackSteps } from "../atoms/useBackClose"
import StepSwap from "../atoms/StepSwap"
import { readLandingChoice } from "../landing/landingChoice"

// Il minimo perché l'app funzioni, e nient'altro: chi si iscrive vuole
// vedere se gli serve, non compilare un'anagrafica. Prezzi e tariffe hanno
// già il loro posto nel profilo e si mettono quando servono.
//
// Due strade, e la domanda che le divide è di fatto, non su sé stessi: il
// codice di un circolo o ce l'hai in mano o no. Chi ce l'ha non si inventa
// un circolo per poi accantonarlo — del questionario gli resta solo la
// parte che parla di lui.
const CHI_SONO = { id: "chi", title: "Come ti chiami?", hint: "Serve per firmare le lezioni." }

// Chi crea un circolo nell'app ne diventa il proprietario, ma non sempre è
// chi lo dirige: spesso è un maestro che l'app l'ha portata lui. Quello che
// risponde qui cambia le domande dopo — chi dirige senza insegnare non ha
// allievi da cui farsi pagare — e cosa trova nell'app: al maestro i Primi
// passi propongono di farla provare alla direzione, a chi dirige senza
// insegnare di far entrare i maestri. Vedi "Chi dirige il circolo" in
// docs/circoli.md.
const CLUB_ROLES = [
	{
		value: "teaches",
		title: "Ci insegno",
		hint: "Il circolo è di altri: quando vuoi, proponi l'app a chi lo dirige",
	},
	{
		value: "runsAndTeaches",
		title: "Lo dirigo, e ci insegno",
		hint: "È tuo: hai la tua giornata, e fai entrare i colleghi",
	},
	{
		value: "runs",
		title: "Lo dirigo, ma non insegno",
		hint: "Direttore, segreteria: guardi le lezioni dei maestri e li fai entrare. Niente prezzi tuoi",
	},
]

const RUOLO = {
	id: "ruolo",
	title: "Che ruolo hai nel circolo?",
	hint: "Cambia cosa ti chiediamo dopo, e cosa trovi nell'app.",
}

const ownSteps = clubRole => [
	CHI_SONO,
	RUOLO,
	clubRole === "runs"
		? { id: "circolo", title: "Il tuo circolo", hint: "Quello che dirigi." }
		: {
				id: "circolo",
				title: "Dove insegni?",
				hint: "Il circolo dove tieni la maggior parte delle lezioni.",
			},
	{ id: "campi", title: "I campi", hint: "Quanti sono e come sono fatti. Si cambiano quando vuoi." },
	// chi non insegna non si fa pagare da nessuno
	...(clubRole === "runs"
		? []
		: [{ id: "pagamenti", title: "Come ti pagano", hint: "Quello che accetti dai tuoi allievi." }]),
]

const INVITE_STEPS = [
	CHI_SONO,
	{
		id: "codice",
		title: "Il codice del circolo",
		hint: "Te l'ha dato chi gestisce il circolo in cui entri.",
	},
]

// Lo stesso modulo serve due volte: alla registrazione, e quando un maestro
// che c'è già aggiunge un circolo — perché insegna in due posti, o perché è
// appena uscito dall'unico che aveva. Cambia solo chi riceve i dati alla
// fine, e che di sé non deve ridire niente.
export function RegistrationModal({ onlySchool = false, closeModal }) {
	const { completeRegistration, createSchool, previewSchoolInvite, logout } =
		useContext(profileContext)
	const { surfaces, paymentMethods, getPaymentMethod, sports } = useContext(sharedDataContext)

	// Quello che si è scelto sul sito prima di registrarsi: chi ha detto "Ho
	// un circolo" parte da "Lo dirigo, e ci insegno", e insegna gli sport che
	// ha toccato. Degli sport si tengono quelli che il catalogo conosce; se
	// non ne resta nessuno, si parte dal tennis come sempre.
	const [landingChoice] = useState(() => (onlySchool ? null : readLandingChoice()))
	const chosenSports = (landingChoice?.sports ?? []).filter(sportID => sports?.[sportID])
	const taughtSports = chosenSports.length > 0 ? chosenSports : [DEFAULT_SPORT]
	// i campi del primo circolo sono dello sport scelto per primo
	const courtSport = taughtSports[0]

	// null finché non ha scelto la strada: è la prima schermata. Chi sta solo
	// aggiungendo un circolo quella scelta l'ha già fatta aprendo questa.
	const [mode, setMode] = useState(onlySchool ? "own" : null)
	const [step, setStep] = useState(0)
	const [isSaving, setIsSaving] = useState(false)

	const [clubRole, setClubRole] = useState(
		landingChoice?.role === "circolo" ? "runsAndTeaches" : "teaches",
	)

	const [inviteCode, setInviteCode] = useState("")
	const [invitePreview, setInvitePreview] = useState(null)
	const [isCheckingCode, setIsCheckingCode] = useState(false)

	const STEPS = onlySchool
		? ownSteps(clubRole).filter(one => one.id !== "chi")
		: mode === "invite"
			? INVITE_STEPS
			: ownSteps(clubRole)
	const stepID = STEPS[step]?.id

	// Il tasto indietro del telefono fa quello del bottone Indietro: un passo
	// indietro, fino alla scelta della strada. La registrazione da finire non
	// si chiude, e cambiare pagina sotto la lascerebbe lì; il circolo nuovo,
	// dal primo passo, si chiude come ogni foglio.
	const depth = onlySchool ? step : mode === null ? 0 : step + 1
	useBackSteps(depth, to => {
		if (onlySchool) setStep(to)
		else if (to === 0) setMode(null)
		else setStep(to - 1)
	})

	const [coachName, setCoachName] = useState("")
	const [schoolName, setSchoolName] = useState("")
	const [address, setAddress] = useState("")
	const [courts, setCourts] = useState({})
	const [methods, setMethods] = useState([])

	const toggle = (list, value) =>
		list.includes(value) ? list.filter(item => item !== value) : [...list, value]

	// Si nasce con un campo: è il minimo perché la schermata della nuova
	// lezione abbia qualcosa da proporre, e da lì si aggiunge il resto.
	const prepareCourts = () => {
		const firstSurface = Object.values(surfaces)[0]
		if (!firstSurface) return {}

		return {
			[courtSport]: courts[courtSport] ?? {
				1: { surfaceType: firstSurface.surfaceType, surfaceName: firstSurface.surfaceName },
			},
		}
	}

	// Senza il passo "chi" nessuno prepara i campi: lo si fa all'apertura.
	useEffect(() => {
		if (!onlySchool) return
		setCourts(prepareCourts())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onlySchool, surfaces])

	const canGoOn = () => {
		if (isSaving) return false

		// senza il catalogo delle superfici non si possono creare campi, e il
		// passo dopo sarebbe un vicolo cieco. Chi entra con un codice i campi
		// non li tocca: il circolo ce li ha già.
		if (stepID === "chi")
			return coachName.trim() !== "" && (mode === "invite" || Object.keys(surfaces).length > 0)
		if (stepID === "codice") return Boolean(invitePreview)
		if (stepID === "ruolo") return true
		if (stepID === "circolo") return schoolName.trim() !== ""
		if (stepID === "campi") return Object.keys(courts).length > 0
		return methods.length > 0
	}

	// Il codice si controlla prima di usarlo: chi lo digita deve sapere in
	// che circolo sta per entrare, non scoprirlo dopo.
	const checkCode = async code => {
		setInviteCode(code)
		setInvitePreview(null)

		if (code.trim().length < 8) return

		setIsCheckingCode(true)
		try {
			setInvitePreview(await previewSchoolInvite(code))
		} catch (err) {
			toast.error(err.message || "Codice non valido", { position: "top-center" })
		} finally {
			setIsCheckingCode(false)
		}
	}

	const goOn = async () => {
		if (stepID === "chi" && mode !== "invite") setCourts(prepareCourts())

		if (step < STEPS.length - 1) {
			setStep(step + 1)
			return
		}

		setIsSaving(true)
		try {
			if (onlySchool) {
				const { schoolName: created } = await createSchool({
					schoolName: schoolName.trim(),
					address: address.trim(),
					courts,
					clubRole,
					availablePaymentMethods: methods,
				})
				toast.success(`${created} è pronto 🎾`, { position: "top-center" })
				closeModal?.()
				return
			}

			await completeRegistration(
				mode === "invite"
					? {
							coachName: coachName.trim(),
							taughtSports,
							inviteCode: inviteCode.trim().toUpperCase(),
						}
					: {
							coachName: coachName.trim(),
							taughtSports,
							schoolName: schoolName.trim(),
							address: address.trim(),
							courts,
							clubRole,
							availablePaymentMethods: methods,
						},
			)
			toast.success("Ci siamo! Buon lavoro 🎾", { position: "top-center" })
		} catch (err) {
			toast.error(err.message || "Non è stato possibile creare il tuo spazio", {
				position: "top-center",
			})
			setIsSaving(false)
		}
	}

	const body = () => {
		switch (stepID) {
			case "chi":
				return (
					<Paragraph title="Nome e cognome">
						<TextInput
							value={coachName}
							placeholder="es. Mario Rossi"
							handleChange={e => setCoachName(e.target.value)}
						/>
					</Paragraph>
				)
			case "codice":
				return (
					<>
						<Paragraph title="Codice">
							<TextInput
								value={inviteCode}
								placeholder="es. ABCD-2345"
								textTransform="uppercase"
								handleChange={e => checkCode(e.target.value)}
							/>
						</Paragraph>
						{isCheckingCode && (
							<Text textSize="caption" textColor={theme.colors.text_light}>
								Controllo...
							</Text>
						)}
						{invitePreview && (
							<Div bg={theme.colors.background} p="1rem" rounded="10px">
								<Text textSize="subheader" textWeight="bold">
									{invitePreview.schoolName}
								</Text>
								<Text textSize="caption" textColor={theme.colors.text_light}>
									{inviteEntryText(invitePreview, "chi ce l'ha invitato")}
								</Text>
							</Div>
						)}
					</>
				)
			case "ruolo":
				return (
					<RadioBlock options={CLUB_ROLES} selected={clubRole} onSelect={setClubRole} />
				)
			case "circolo":
				return (
					<>
						<Paragraph title="Nome del circolo">
							<TextInput
								value={schoolName}
								placeholder="es. Tennis Club Bologna"
								handleChange={e => setSchoolName(e.target.value)}
							/>
						</Paragraph>
						<Paragraph title="Indirizzo (facoltativo)">
							<TextInput
								value={address}
								placeholder="serve per il navigatore sugli eventi"
								handleChange={e => setAddress(e.target.value)}
							/>
						</Paragraph>
					</>
				)
			case "campi":
				return (
					<SchoolCourtsEditor
						sportID={courtSport}
						courts={courts}
						surfaces={surfaces}
						isEditing={true}
						onChange={setCourts}
					/>
				)
			default:
				return (
					<Div d="flex" flexWrap="wrap" m={{ b: "1rem" }} style={{ gap: "0.5rem" }}>
						{Object.keys(paymentMethods).map(methodID => (
							<ToggleChip
								key={methodID}
								size="lg"
								text={getPaymentMethod(methodID)?.paymentName ?? methodID}
								isSelected={methods.includes(methodID)}
								onClick={() => setMethods(toggle(methods, methodID))}
							/>
						))}
					</Div>
				)
		}
	}

	// La prima domanda non è su di sé — "sei un maestro solo o gestisci un
	// centro?" nessuno sa rispondere il primo giorno — ma su un fatto: il
	// codice o ce l'hai o no.
	if (mode === null) {
		return (
			<Container>
				<Div m={{ b: "0.25rem" }} textWeight="bold" textSize="title">
					Da dove cominciamo?
				</Div>
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
					Puoi cambiare idea dopo: un circolo si crea quando serve, e in uno esistente
					si entra quando qualcuno ti passa il codice.
				</Text>

				<Div d="flex" flexDir="column" style={{ gap: "0.75rem" }}>
					{[
						{
							id: "own",
							title: "Creo il mio circolo",
							hint: "Dove insegni, i campi, quanto fai pagare. Sono cose tue.",
						},
						{
							id: "invite",
							title: "Ho il codice di un circolo",
							hint: "Te l'ha dato chi lo gestisce. Entri e trovi tutto già pronto.",
						},
					].map(choice => (
						<motion.div key={choice.id} whileTap={{ scale: 0.99 }}>
							<Div
								p="1rem"
								rounded="12px"
								cursor="pointer"
								border="2px solid"
								borderColor={theme.colors.gray_light}
								onClick={() => setMode(choice.id)}>
								<Text textSize="subheader" textWeight="bold">
									{choice.title}
								</Text>
								<Text textSize="caption" textColor={theme.colors.text_light}>
									{choice.hint}
								</Text>
							</Div>
						</motion.div>
					))}
				</Div>

				<Div textAlign="center" m={{ t: "1.5rem" }} cursor="pointer" onClick={() => logout()}>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						Esci e riprendi più tardi
					</Text>
				</Div>
			</Container>
		)
	}

	return (
		<Container>
			<Div m={{ b: "0.25rem" }} textWeight="bold" textSize="title">
				{STEPS[step].title}
			</Div>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
				{STEPS[step].hint}
			</Text>

			<Div maxH="55vh" overflow="auto">
				<Container>
					<StepSwap value={step}>
						{body()}
					</StepSwap>
				</Container>
			</Div>

			{/* dove sei arrivato, senza contarli */}
			<Div d="flex" justify="center" m={{ t: "1rem" }} style={{ gap: "0.4rem" }}>
				{STEPS.map((_, index) => (
					<Div
						key={index}
						w={index === step ? "1.5rem" : "0.5rem"}
						h="0.5rem"
						rounded="circle"
						transition
						bg={index <= step ? theme.colors.gunmetal : theme.colors.gray_light}
					/>
				))}
			</Div>

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							textSize="subheader"
							text="Indietro"
							disabled={isSaving}
							action={() => (step === 0 ? setMode(null) : setStep(step - 1))}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.gunmetal_fill}
							textColor="white"
							textSize="subheader"
							text={
								isSaving
									? "Ci siamo..."
									: step < STEPS.length - 1
										? "Avanti"
										: mode === "invite"
											? "Entra nel circolo"
											: "Crea il tuo spazio"
							}
							disabled={!canGoOn()}
							action={goOn}
						/>
					</Col>
				</Row>
			</Div>


		</Container>
	)
}

export default RegistrationModal
